import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { sampleData } from '../../data/sampleData';
import MarketingReport from '../MarketingReport';
import { usePrintHook } from '../../hooks/usePrintHook';
import { apiService } from '../../services/apiService';
import { Report } from '../../types/reportTypes';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { InlineDemoNotice } from '../common/DemoModeIndicator';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const reportRef = useRef<HTMLDivElement>(null);
  const [latestReport, setLatestReport] = useState<Report | null>(null);
  const [useMockData, setUseMockData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { printRef } = usePrintHook();

  // Fetch latest report
  useEffect(() => {
    const fetchLatestReport = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const reports = await apiService.getReports();
        
        if (reports && reports.length > 0) {
          // Sort by creation date (newest first)
          const sortedReports = reports.sort((a: { createdAt: string }, b: { createdAt: string }) => {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          });
          
          // Get the most recent report
          const latestReport = await apiService.getReport(sortedReports[0].id);
          setLatestReport(latestReport);
          setUseMockData(false);
        } else {
          // If no reports exist, use sample data
          setLatestReport({
            id: 'sample-report',
            title: 'Sample Marketing Report',
            reportType: 'monthly',
            period: { month: 3, year: 2023 },
            createdBy: 'sample-user',
            data: sampleData,
            createdAt: new Date()
          });
          setUseMockData(true);
        }
      } catch (err) {
        console.error('Error fetching reports:', err);
        setError('Failed to load reports. Using sample data instead.');
        
        // Fallback to sample data
        setLatestReport({
          id: 'sample-report',
          title: 'Sample Marketing Report',
          reportType: 'monthly',
          period: { month: 3, year: 2023 },
          createdBy: 'sample-user',
          data: sampleData,
          createdAt: new Date()
        });
        setUseMockData(true);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLatestReport();
  }, []);

  // Generate PDF from report
  const generatePDF = async () => {
    if (!reportRef.current) return;
    
    try {
      // Show loading state
      setLoading(true);
      
      // Create filename for the PDF
      const fileName = latestReport
        ? `ROI_Marketing_Report_${latestReport.period.month}_${latestReport.period.year}.pdf` 
        : 'marketing_report.pdf';
      
      // Configure dimensions
      const reportElement = reportRef.current;
      const canvas = await html2canvas(reportElement, {
        scale: 1.5, // Higher scale for better quality
        useCORS: true, // Allow images from other domains
        logging: false,
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // Page setup (landscape A4)
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Calculate ratio to fit content to PDF
      const canvasRatio = canvas.height / canvas.width;
      const contentWidth = pdfWidth;
      const contentHeight = contentWidth * canvasRatio;
      
      // Add content spread across multiple pages if needed
      let heightLeft = contentHeight;
      let position = 0;
      let page = 1;
      
      pdf.addImage(imgData, 'PNG', 0, position, contentWidth, contentHeight);
      heightLeft -= pdfHeight;
      
      // Add more pages if content overflows
      while (heightLeft > 0) {
        position = -pdfHeight * page;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, contentWidth, contentHeight);
        heightLeft -= pdfHeight;
        page++;
      }
      
      // Add footer on each page
      for (let i = 0; i < page; i++) {
        pdf.setPage(i + 1);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`ROI Exteriors Marketing Report - Page ${i + 1} of ${page}`, 10, pdfHeight - 5);
      }
      
      // Download PDF
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle export to PDF
  const handleExportPDF = () => {
    generatePDF();
  };
  
  // Handle print
  const handlePrint = () => {
    if (reportRef.current) {
      // Cast the ref to the expected type
      printRef(reportRef as unknown as React.RefObject<HTMLElement>);
    }
  };

  // Get month name from month number
  const getMonthName = (month: number): string => {
    return new Date(2000, month - 1).toLocaleString('default', { month: 'long' });
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h1 className="text-2xl font-bold mb-4">Welcome, {user?.firstName || 'Guest'}!</h1>
        <p className="text-gray-600">
          Use the ROI Exteriors Marketing Portal to view performance reports,
          analyze marketing data, and make data-driven decisions for your business.
        </p>
        
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded shadow-sm flex flex-col items-center text-center">
            <h3 className="text-lg font-semibold text-blue-800 mb-2">View Reports</h3>
            <p className="text-sm mb-3">Access all your marketing performance reports and analytics.</p>
            <Link 
              to="/reports" 
              className="text-blue-600 hover:text-blue-800 font-medium flex items-center justify-center"
            >
              View Reports
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </Link>
          </div>
          
          <div className="bg-green-50 p-4 rounded shadow-sm flex flex-col items-center text-center">
            <h3 className="text-lg font-semibold text-green-800 mb-2">Upload Data</h3>
            <p className="text-sm mb-3">Upload your sales and ad spend data to generate new reports.</p>
            <Link 
              to="/upload" 
              className="text-green-600 hover:text-green-800 font-medium flex items-center justify-center"
            >
              Upload Data
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </Link>
          </div>
          
          <div className="bg-purple-50 p-4 rounded shadow-sm flex flex-col items-center text-center">
            <h3 className="text-lg font-semibold text-purple-800 mb-2">Account Settings</h3>
            <p className="text-sm mb-3">Manage your account settings and preferences.</p>
            <Link 
              to="/profile" 
              className="text-purple-600 hover:text-purple-800 font-medium flex items-center justify-center"
            >
              View Profile
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </Link>
          </div>
        </div>
        
        {/* Static Reports Quick Links */}
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Static Reports</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Link 
              to="/january-report" 
              className="text-center px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
            >
              January 2025
            </Link>
            <Link 
              to="/february-report" 
              className="text-center px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm"
            >
              February 2025
            </Link>
            <Link 
              to="/dashboard-march-report" 
              className="text-center px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
            >
              March 2025
            </Link>
            <Link 
              to="/april-report" 
              className="text-center px-3 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 text-sm"
            >
              April 2025
            </Link>
            <Link 
              to="/may-report" 
              className="text-center px-3 py-2 bg-rose-600 text-white rounded hover:bg-rose-700 text-sm"
            >
              May 2025
            </Link>
            <Link 
              to="/cc-january-report" 
              className="text-center px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
            >
              CC-Jan 2025
            </Link>
            <Link 
              to="/cc-february-report" 
              className="text-center px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm"
            >
              CC-Feb 2025
            </Link>
            <Link 
              to="/cc-march-report" 
              className="text-center px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
            >
              CC-Mar 2025
            </Link>
            <Link 
              to="/cc-april-report" 
              className="text-center px-3 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 text-sm"
            >
              CC-Apr 2025
            </Link>
            <Link 
              to="/cc-may-report" 
              className="text-center px-3 py-2 bg-rose-600 text-white rounded hover:bg-rose-700 text-sm"
            >
              CC-May 2025
            </Link>
            <Link 
              to="/q1-report" 
              className="text-center px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
            >
              Q1 2025
            </Link>
            <Link 
              to="/cc-q1-report" 
              className="text-center px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
            >
              CC-Q1 2025
            </Link>
          </div>
        </div>
      </div>
      
      {loading && !latestReport ? (
        <div className="bg-white rounded-lg shadow-md p-6 flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="mb-4 flex flex-col sm:flex-row justify-between items-center gap-4">
            <h2 className="text-xl font-bold text-gray-800 text-center sm:text-left">
              {useMockData ? 'Sample Marketing Report' : latestReport?.title} 
              {latestReport?.period && !useMockData ? 
                ` (${getMonthName(latestReport.period.month)} ${latestReport.period.year})` : ''}
            </h2>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={handlePrint}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path>
                </svg>
                <span className="whitespace-nowrap">Print</span>
              </button>
              <button
                onClick={handleExportPDF}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                <span className="whitespace-nowrap">Export as PDF</span>
              </button>
              <Link
                to="/upload"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                <span className="whitespace-nowrap">Create New Report</span>
              </Link>
            </div>
          </div>
          
          {/* Show error message */}
          {error && (
            <div className="border p-4 rounded shadow-sm mb-4 bg-yellow-50 text-yellow-700">
              <h3 className="text-lg font-semibold mb-2">⚠️ {error}</h3>
              <p className="mb-3">
                Showing sample data for demonstration purposes.
              </p>
            </div>
          )}
          
          {/* Show sample data mode indicator */}
          {useMockData && !error && (
            <InlineDemoNotice>
              <p>
                This is a demonstration using sample data. You can upload your own data 
                to generate custom reports, or run the application locally with the Laravel 
                backend to access real reports.
              </p>
            </InlineDemoNotice>
          )}
          
          {/* Loading overlay for PDF generation */}
          {loading && latestReport && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-center">Generating PDF...</p>
              </div>
            </div>
          )}
          
          {/* Wrap report in a div with ref for PDF export */}
          {latestReport && latestReport.data && (
            <div ref={reportRef}>
              <MarketingReport data={latestReport.data} />
            </div>
          )}
          
          {/* Show a message if data is missing */}
          {latestReport && !latestReport.data && (
            <div className="p-6 bg-red-50 text-red-700 rounded-lg">
              <h3 className="text-lg font-semibold mb-2">Error: Missing Report Data</h3>
              <p>The report data is incomplete or incorrectly formatted. Please try uploading new data.</p>
              <Link to="/upload" className="mt-4 inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                Upload New Data
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;